import {
    Button,
    Flex,
    Grid,
    Loader,
    MultiSelect,
    Select,
    Stack,
    TextInput,
    Radio,
} from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "@mantine/form";
import { isValidPhoneNumber } from "react-phone-number-input";
import axios from "axios";

import MainLayout from "../../layouts/MainLayout/MainLayout";
import PhoneInputField from "../../components/PhoneInputField/PhoneInputField";
import { useAppContext } from "../../context/AppState/AppContext";
import showNotification from "../../utils/showNotification";
import { IconAward, IconMedal, IconMedal2, IconNumber2, IconTrophy } from "@tabler/icons-react";

const API = process.env.REACT_APP_BACKEND_SERVER;

export default function Sorteo() {
    const { slug } = useParams();
    const { specialtiesInfo } = useAppContext();

    const [raffle, setRaffle] = useState(null);
    const [loadingRaffle, setLoadingRaffle] = useState(true);
    const [loadError, setLoadError] = useState(null);

    const [specialtiesData, setSpecialtiesData] = useState([]);
    const [selectedValue, setSelectedValue] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState(null);

    const phoneRef = useRef(null);

    const form = useForm({
        initialValues: {
            name: "",
            email: "",
            phone: "",
            alreadyStudent: "NO",
            specialties: [],
        },
        validate: {
            name: (value) =>
                value.trim().length > 5 ? null : "Ingresá tu nombre completo",
            email: (value) =>
                /^\S+@\S+$/.test(value) ? null : "El email ingresado no es válido",
            phone: (value) =>
                typeof value === "string" && isValidPhoneNumber(value)
                    ? null
                    : "Ingresá un teléfono válido",
        },
    });

    // Especialidades (del contexto, como antes)
    useEffect(() => {
        if (specialtiesInfo?.data) {
            setSpecialtiesData(
                specialtiesInfo.data.map((specialty) => specialty.name.toUpperCase())
            );
        }
    }, [specialtiesInfo]);

    // Sorteo por slug. Sin slug, cae al sorteo vigente.
    useEffect(() => {
        let cancelled = false;

        const fetchRaffle = async () => {
            setLoadingRaffle(true);
            setLoadError(null);
            setResult(null);
            setSelectedValue(null);

            try {
                const endpoint = slug
                    ? `${API}/info/raffles/${slug}`
                    : `${API}/info/raffles/current`;
                const { data } = await axios.get(endpoint);
                if (!cancelled) setRaffle(data);
            } catch (error) {
                if (!cancelled) {
                    setRaffle(null);
                    setLoadError({
                        status: error.response?.status ?? 500,
                        message: error.response?.data?.message ?? null,
                    });
                }
            } finally {
                if (!cancelled) setLoadingRaffle(false);
            }
        };

        fetchRaffle();
        return () => {
            cancelled = true;
        };
    }, [slug]);

    const handleSubmit = async (values) => {
        setIsLoading(true);

        try {
            const payload = {
                name: values.name,
                email: values.email,
                phone: values.phone,
                alreadyStudent: values.alreadyStudent,
                interestAreas: Array.isArray(values.specialties)
                    ? values.specialties
                    : [values.specialties].filter(Boolean),
                questionId: raffle?.question?._id ?? null,
                selectedValue,
            };


            const { data } = await axios.post(
                `${API}/info/raffles/${raffle.slug}/participate`,
                payload
            );

            setResult(data);
            showNotification({
                title: data.title ?? "¡Ya estás participando del sorteo!",
                message: data.message,
                color: "green",
                status: "success",
            });
        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "Error",
                message:
                    error.response?.data?.message ??
                    "No pudimos registrar tu participación. Probá de nuevo.",
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (loadingRaffle) {
        return (
            <MainLayout>
                <div className="SubscriptionPage-main-container">
                    <Flex justify="center" py={80}>
                        <Loader color="#FF7BAC" />
                    </Flex>
                </div>
            </MainLayout>
        );
    }

    if (loadError) {
        return (
            <MainLayout>
                <div className="SubscriptionPage-main-container">
                    <h1 className="landingHero__title">
                        Sorteo <br />
                        <span className="landingHero__gradient">no disponible</span>
                    </h1>
                    <p className="SubscriptionPage--description">
                        {loadError.status === 404
                            ? "No encontramos este sorteo. Revisá el enlace."
                            : loadError.message ?? "Este sorteo ya no está activo."}
                    </p>
                </div>
            </MainLayout>
        );
    }

    const { content = {}, form: formConfig = {}, question } = raffle;

    // Compatibilidad: prizes es el formato nuevo, prizeDescription el viejo
    const prizes = content.prizes?.length
        ? content.prizes
        : content.prizeDescription
            ? [{ position: 1, title: content.prizeDescription, description: "" }]
            : [];

    return (
        <MainLayout>
            <div className="SubscriptionPage-main-container">
                <h1 className="landingHero__title">
                    {content.heading ?? "Tenemos"} <br />
                    <span className="landingHero__gradient">
                        {content.highlight ?? "sorteo"}
                    </span>
                </h1>

                <Stack mb={20}>
                    {content.description && (
                        <p className="SubscriptionPage--description">{content.description}</p>
                    )}
                    {content.secondaryDescription && (
                        <p
                            className="SubscriptionPage--description"
                            style={{ marginTop: "-16px" }}
                        >
                            {content.secondaryDescription}
                        </p>
                    )}
                </Stack>

                {prizes.length > 0 && (
                    <div style={{ width: "100%", maxWidth: "768px", margin: "0 auto 32px" }}>
                        {prizes.map((prize, idx) => (
                            <div
                                key={prize.position ?? prize.title}
                                style={{
                                    borderLeft: '4px solid var(--color-tertiary)',
                                    backgroundColor: 'var(--color-tertiary-light, #f5f5f5)',
                                    padding: '1rem 1.25rem',
                                    margin: '1rem 0',
                                    borderRadius: '4px',
                                }}
                            >
                                <Flex>
                                    {idx === 0 
                                        ? <IconTrophy style={{flexShrink:0}} color="var(--color-text-primary)"/> 
                                        :
                                        idx === 1 ? <IconAward style={{flexShrink:0}} color="var(--color-text-primary)"/>
                                        :  <IconMedal2 style={{flexShrink:0}} color="var(--color-text-primary)"/>
                                    }
                                    <Stack>
                                        <p
                                    className="SubscriptionPage--description"
                                    style={{ fontWeight: 600, margin: 0 }}
                                >
                                    {prize.title}
                                </p>
                                {prize.description && (
                                    <p
                                        className="SubscriptionPage--description"
                                        style={{ margin: '4px 0 0', opacity: 0.85 }}
                                    >
                                        {prize.description}
                                    </p>
                                )}
                                    </Stack>
                                </Flex>
                            </div>
                        ))}
                    </div>
                )}
                {result ? (
                    <Stack align="center" mt={32} style={{ maxWidth: "768px", margin: "0 auto" }}>
                        <h2
                            style={{
                                fontSize: "var(--fs-body)",
                                fontWeight: 600,
                                textAlign: "center",
                                color: "var(--color-text-primary)",
                            }}
                        >
                            {result.title ?? "¡Ya estás participando del sorteo!"}
                        </h2>
                        <p className="SubscriptionPage--description">{result.message}</p>
                    </Stack>
                ) : (
                    <form
                        style={{
                            width: "100%",
                            maxWidth: "768px",
                            margin: "0 auto",
                            color: "var(--color-text-primary)",
                        }}
                        onSubmit={form.onSubmit(handleSubmit)}
                    >
                        <Grid>
                            <Grid.Col span={6}>
                                <TextInput
                                    label="Escribí tu nombre"
                                    placeholder="Juan Pérez"
                                    required
                                    {...form.getInputProps("name")}
                                />
                            </Grid.Col>

                            <Grid.Col span={6}>
                                <TextInput
                                    label="Escribí tu email"
                                    placeholder="juan@mail.com"
                                    required
                                    {...form.getInputProps("email")}
                                />
                            </Grid.Col>

                            <Grid.Col span={12}>
                                <PhoneInputField
                                    ref={phoneRef}
                                    required
                                    name="phone"
                                    fieldError={form.errors.phone}
                                    {...form.getInputProps("phone")}
                                />
                            </Grid.Col>

                            {formConfig.showSpecialties !== false && specialtiesInfo && (
                                <Grid.Col span={12}>
                                    <MultiSelect
                                        data={specialtiesData}
                                        label="Especialidades de interés"
                                        {...form.getInputProps("specialties")}
                                    />
                                </Grid.Col>
                            )}

                            {formConfig.showAlreadyStudent !== false && (
                                <Grid.Col span={12}>
                                    <Radio.Group
                                        name="alreadyStudent"
                                        value={form.values.alreadyStudent}
                                        onChange={(value) =>
                                            form.setFieldValue("alreadyStudent", value)
                                        }
                                        label="¿Participaste en alguna capacitación de FORVET?"
                                        withAsterisk
                                    >
                                        <Radio value="SI" label="Sí" />
                                        <Radio value="NO" label="No" />
                                    </Radio.Group>
                                </Grid.Col>
                            )}

                            {question && (
                                <Grid.Col mt={16} span={12}>
                                    <h3
                                        style={{
                                            fontSize: "var(--fs-body)",
                                            textAlign: "center",
                                            fontWeight: 400,
                                            marginTop: "32px",
                                        }}
                                    >
                                        {content.questionPrompt ??
                                            "¿Te animás a responder? Si acertás, sumás chances"}
                                    </h3>

                                    <h3
                                        style={{
                                            fontSize: "var(--fs-body)",
                                            border: "1px solid var(--color-text-primary)",
                                            padding: "1rem",
                                            borderRadius: "var(--border-radius-md)",
                                            fontWeight: 400,
                                            margin: "1rem 0",
                                            fontStyle: "italic",
                                        }}
                                    >
                                        {`"${question.questionText}"`}
                                    </h3>

                                    <Select
                                        placeholder="Seleccioná una respuesta"
                                        value={selectedValue}
                                        onChange={setSelectedValue}
                                        data={question.responseOptions.map((item) => ({
                                            value: String(item.value),
                                            label: item.label,
                                        }))}
                                    />
                                </Grid.Col>
                            )}

                            <Grid.Col span={12} mt={16}>
                                <Button
                                    fullWidth
                                    color="#FF7BAC"
                                    type="submit"
                                    loading={isLoading}
                                    disabled={isLoading}
                                >
                                    <span style={{ fontSize: "var(--fs-small)" }}>
                                        {content.buttonLabel ?? "Participar"}
                                    </span>
                                </Button>
                            </Grid.Col>
                        </Grid>
                    </form>
                )}
            </div>
        </MainLayout>
    );
}